import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import Logo from "../../components/Logo";
import { useAuth } from "../../store/authEtablissementStore";

const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

export default function EtablissementLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const state = location.state as { message?: string } | null;
    if (state?.message) {
      setSuccessMessage(state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);
  const emailValid = useMemo(() => emailRegex.test(email.trim()), [email]);
  const canSubmit = emailValid && password.length > 0 && !submitting;

  const PENDING_EMAIL_KEY = "agbaza.login.pendingEmail";

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    setError("");
    setSubmitting(true);
    const result = await signIn({ email: email.trim(), password });
    setSubmitting(false);
    if (result.ok) {
      const params = new URLSearchParams(location.search);
      const redirect = params.get("redirect");
      if (result.requiresOtp) {
        sessionStorage.setItem(PENDING_EMAIL_KEY, email.trim());
        navigate("/etablissement/connexion/code", {
          replace: true,
          state: { email: email.trim(), redirect: redirect ?? undefined },
        });
      } else {
        navigate(redirect && redirect.startsWith("/etablissement/") ? redirect : "/etablissement/dashboard/lits", { replace: true });
      }
    } else {
      const notValidated = /non valid/i.test(result.message ?? "");
      setError(
        notValidated
          ? "Votre établissement n'est pas encore validé par l'administrateur. Vous serez notifié dès que l'accès sera activé."
          : result.message || "Identifiants incorrects"
      );
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 relative">
      {/* Mobile background image */}
      <div className="absolute inset-0 md:hidden">
        <img src={'/maps-login-side.jpg'} alt="login" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-white/30" />
      </div>

      {/* Left: form */}
      <div className="relative flex items-center justify-center px-4 md:px-10 py-10">
        <div className="w-full max-w-md">
          <div className="flex justify-center"><Logo size={56} /></div>
          <h1 className="mt-6 text-2xl md:text-3xl font-semibold text-[#0B1F16] text-center md:text-left">
            Connectez-vous en tant qu&apos;hôpital
          </h1>
          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            {successMessage && (
              <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-[10px] px-3 py-2">
                {successMessage}
              </div>
            )}
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm text-[#0B1F16]">Email</label>
              <input
                type="email"
                autoComplete="email"
                placeholder="exemple@domaine.com"
                value={email}
                onChange={(event) => setEmail(event.target.value.replace(/\s/g, ""))}
                className={`w-full h-12 rounded-[10px] border px-3 bg-white ${emailValid || email === "" ? 'border-[#E5E7EB]' : 'border-red-400'}`}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm text-[#0B1F16]">Mot de passe</label>
                <Link to="/etablissement/mot-de-passe/oubli" className="text-sm" style={{ color: "#00553E" }}>
                  Mots de passe oublié ?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full h-12 rounded-[10px] border px-3 pr-12 bg-white"
                  style={{ borderColor: "#E5E7EB" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded text-gray-500 hover:text-[#0B1F16] focus:outline-none focus:ring-2 focus:ring-[#00553E]/30"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-600">
              En cliquant sur le bouton, vous acceptez nos <u>conditions d'utilisation</u> et notre <u>politique de confidentialité</u>.
            </p>
            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full h-12 rounded-[10px] font-semibold cursor-pointer disabled:opacity-60"
              style={{ backgroundColor: "#00553E", color: "#FFFFFF" }}
            >
              {submitting ? "Connexion…" : "Connectez-vous"}
            </button>
            <div className="text-sm text-center md:text-left">
              Vous n'avez pas encore de compte ?{" "}
              <Link className="font-semibold" style={{ color: "#00553E" }} to="/etablissement/inscription/recherche">
                Enregistrez-vous
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* Right: image (desktop) */}
      <div className="relative hidden md:block">
        <img src={'/maps-login-side.jpg'} alt="login" className="absolute inset-0 w-full h-full object-cover" />
      </div>
    </div>
  );
}
